import React, { useCallback, useEffect, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { MomentsContext } from './MomentsContext';
import { Moment } from './moments.types';
import {
  productListProductToGlobalProduct,
  useBannerAnimation,
} from './momentsUtils';
import { useTranslation } from 'react-i18next';

type MomentsProviderProps = {
  subscriptionId: string;
  refetchSubscription: ({
    compareOnlyLines,
  }: {
    compareOnlyLines?: boolean;
  }) => void;
  showErrorToast: (message: string) => void;
};

export const MomentsStubContextProvider = ({
  value,
  children,
}: {
  value: MomentsProviderProps;
  children: React.ReactElement | React.ReactElement[];
}): React.ReactElement => {
  const { onAnimationEnd, className } = useBannerAnimation();
  const { t } = useTranslation();
  const [momentData, setMomentData] = useState<Moment & { actions: any[] }>(
    {} as Moment & { actions: any[] },
  );
  const { showErrorToast } = value;
  const [productList, setProductList] = useState<GlobalProduct[] | null>(null);
  const [isProductListModalOpen, setIsProductListModalOpen] = useState(false);
  const handleOpenProductListModal = useCallback(() => {
    setIsProductListModalOpen(true);
  }, []);

  const handleCloseProductListModal = useCallback(() => {
    setIsProductListModalOpen(false);
    setProductList(null);
  }, []);
  useEffect(() => {
    const origin = window.opener;
    const target = process.env.SHOPIFY_APP_PUBLIC_URL;
    if (!isEmpty(momentData) || !origin || !target) return;

    origin.postMessage('moment', target);

    const handleMomentData = (event) => {
      if (event.origin !== target) {
        return;
      }

      try {
        const _moment = JSON.parse(event.data);
        setMomentData(_moment);
      } catch (e) {
        console.log(e);
        showErrorToast(t('moments.claimErrorToast'));
      }
    };

    window.addEventListener('message', handleMomentData);

    return () => {
      window.removeEventListener('message', handleMomentData);
    };
  }, []);

  const dismissReward = () => null;

  let isClaimable = false;
  if (momentData?.actions?.[0] as { isAutomatic: boolean }) {
    isClaimable = !(momentData.actions[0] as any).isAutomatic;
  }

  const _moment = {
    id: 'stub-moment-id',
    isEarned: true,
    isClaimed: false,
    canBeClaimed: isClaimable,
    earnedDates: [],
    unearnedDates: [],
    claimedDates: [],
    moment: momentData,
  };

  const handleClaimReward = () => {
    const isProductList = momentData.actions.some(
      ({ metadata }) => metadata?.productVariantList.length > 0,
    );
    if (isProductList) {
      const productsToShow = _moment.moment.availableProductList.map(
        (product) => {
          const globalProduct = productListProductToGlobalProduct(product);
          const imgs = product?.images?.map(({ url }) => url);

          return {
            ...globalProduct,
            imgs,
          };
        },
      );

      setProductList(productsToShow);
      setIsProductListModalOpen(true);
    }
  };

  return (
    <MomentsContext.Provider
      value={{
        moments: [_moment],
        momentsForClaim: isClaimable ? [_moment] : [],
        momentsForDismiss: !isClaimable ? [_moment] : [],
        handleClaimReward,
        dismissReward,
        setSwiperRef: () => null,
        onAnimationEnd,
        className,
        isLoading: false,
        isClaiming: false,
        productList,
        isProductListModalOpen,
        handleOpenProductListModal,
        handleCloseProductListModal,
        handleClaimProductListReward: () => null,
      }}
    >
      {children}
    </MomentsContext.Provider>
  );
};
